import { isThaiLocale } from '../../locale/localeUtils';
import { Weekday } from '../Calendar/DisabledDayFilters';
import { isLeapYear } from '../Calendar/GregorianCalendarFacts';
const MINIMUM_YEAR = -271821;
export const EPOCH = new Date(1970, 0, 1, 0, 0, 0, 0);
export const MAX_HOURS = 2399999995; // max duration in hours
export const MIN_HOURS = -2399999991; // min duration in hours
export const MAX_MINUTES = 4228629119; // max duration in minutes
export const MIN_MINUTES = -4228629119; // min duration in minutes
export const NEGATIVE_HOURS_THRESHOLD = -754907; // threshold for negative duration hours
export const NEGATIVE_MINUTES_THRESHOLD = -45294476; // threshold for negative duration minutes
export const GMT_OFFSET_MINUTES = 3; // GMT offset in minutes for GMT-0456 if the negative duration hour value is less than NEGATIVE_HOURS_THRESHOLD
export const GMT_OFFSET_SECONDS = 58; // GMT offset in seconds for GMT-0456 if the negative duration hour value is less than NEGATIVE_HOURS_THRESHOLD
export const GMT_OFFSET_ADJUSTMENT = 4; // GMT offset adjustment in minutes for GMT-0456 if the negative duration minute value is less than NEGATIVE_MINUTES_THRESHOLD
export const MAX_TIME = new Date(MAX_HOURS * 3600 * 1000).getTime() + EPOCH.getTime();
export const MIN_TIME = new Date(MIN_HOURS * 3600 * 1000).getTime() + EPOCH.getTime();
export const MILLISECONDS_IN_HOUR = 3600000;
export function getYearOrder(localeTag) {
  const parts = new Intl.DateTimeFormat(localeTag, {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric'
  }).formatToParts(new Date());
  const yearIndex = parts.findIndex(({
    type
  }) => type === 'year');
  const monthIndex = parts.findIndex(({
    type
  }) => type === 'month');
  return yearIndex > monthIndex ? 'after' : 'before';
}
export function getMonthsForLocale(localeTag) {
  const format = new Intl.DateTimeFormat(localeTag, {
    month: 'long'
  });
  const months = [];
  for (let month = 0; month < 12; month++) {
    // Because there is a offset bug in Safari when using Intl.DateTimeFormat, setting
    // day to 2nd day of month to avoid the issue
    const testDate = new Date(0, month, 2, 0, 0, 0);
    months.push(format.format(testDate));
  }
  return months;
}
export function getDaysOfWeek(localeTag) {
  const format = new Intl.DateTimeFormat(localeTag, {
    weekday: 'narrow'
  });
  const weekdays = [];
  for (let day = 5; day < 12; day++) {
    const testDate = new Date(2020, 0, day, 0, 0, 0);
    weekdays.push(format.format(testDate));
  }
  return weekdays;
}
export function getDayPeriods(localeTag) {
  const format = new Intl.DateTimeFormat(localeTag, {
    hour: 'numeric',
    minute: 'numeric',
    second: 'numeric'
  });
  const dayPeriods = [];
  let testDate = new Date(2020, 0, 5, 0, 0, 0); // AM
  let parts = format.formatToParts(testDate);
  let item = parts.find(item => item.type === 'dayPeriod');
  item && dayPeriods.push(item.value);
  testDate = new Date(2020, 0, 5, 13, 0, 0); // PM
  parts = format.formatToParts(testDate);
  item = parts.find(item => item.type === 'dayPeriod');
  item && dayPeriods.push(item.value);
  return dayPeriods;
}
export function getDayPeriodOrder(localeTag) {
  const parts = new Intl.DateTimeFormat(localeTag, {
    hour: 'numeric',
    minute: 'numeric',
    second: 'numeric'
  }).formatToParts(new Date());
  const dayPeriodIndex = parts.findIndex(({
    type
  }) => type === 'dayPeriod');
  if (dayPeriodIndex === -1) {
    // locale does not use a 12 hr clock (we assume 24 hr clock in this case).
    return 'none';
  }
  const hourIndex = parts.findIndex(({
    type
  }) => type === 'hour');
  return dayPeriodIndex > hourIndex ? 'after' : 'before';
}
export function getTimeSeparator(localeTag) {
  const parts = new Intl.DateTimeFormat(localeTag, {
    hour: 'numeric',
    minute: 'numeric',
    second: 'numeric'
  }).formatToParts(new Date());

  // Sometimes the first 'literal' value does not represent the time separator.
  const hourIndex = parts.findIndex(({
    type
  }) => type === 'hour');
  const separator = parts.slice(hourIndex).find(({
    type
  }) => type === 'literal');
  return separator ? separator.value : ':';
}
export function getHoursSeparator(localeTag) {
  const parts = new Intl.DateTimeFormat(localeTag, {
    hour: 'numeric',
    minute: 'numeric',
    second: 'numeric'
  }).formatToParts(new Date());
  const hourIndex = parts.findIndex(({
    type
  }) => type === 'hour');
  const separator = parts.slice(hourIndex).find(({
    type
  }) => type === 'literal');
  return separator ? separator.value : ':';
}
export function getMinutesSeparator(localeTag) {
  const parts = new Intl.DateTimeFormat(localeTag, {
    hour: 'numeric',
    minute: 'numeric',
    second: 'numeric'
  }).formatToParts(new Date());
  const minuteIndex = parts.findIndex(({
    type
  }) => type === 'minute');
  const separator = parts.slice(minuteIndex).find(({
    type
  }) => type === 'literal');
  return separator ? separator.value : ':';
}
export function getSecondsSeparator(localeTag) {
  const parts = new Intl.DateTimeFormat(localeTag, {
    hour: 'numeric',
    minute: 'numeric',
    second: 'numeric'
  }).formatToParts(new Date());
  const secondIndex = parts.findIndex(({
    type
  }) => type === 'second');
  const separator = parts.slice(secondIndex).find(({
    type
  }) => type === 'literal');
  return separator ? separator.value : '';
}
export function getMonthAndYearByDate(localeTag, date) {
  if (date) {
    const returnVal = new Intl.DateTimeFormat(localeTag, {
      year: 'numeric',
      month: 'short'
    }).format(date);
    return returnVal;
  }
  return '';
}
export function isSameDay(a, b) {
  return a.getDate() === b.getDate() && a.getMonth() === b.getMonth() && a.getFullYear() === b.getFullYear();
}

// To convert English (Gregorian calendar) year to Thai year (Buddhist Calendar): Add 543 years.
export function convertGregorianYearToThaiYear(yearValue) {
  return yearValue + 543;
}

// To convert Thai year (Buddhist Calendar) to English (Gregorian calendar) year : Subtract 543 years.
export function convertThaiYearToGregorianYear(yearValue) {
  return yearValue >= 543 ? yearValue - 543 : 0;
}

// returns a day of the year in range [1, 365] (366 for leap years)
export const getDayNumber = date => {
  const [year, monthIndex, day] = [date.getFullYear(), date.getMonth(), date.getDate()];
  return [0, 31, isLeapYear(year) ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30].slice(0, monthIndex + 1).reduce((s, n) => s + n) + day;
};

// Returns the first day of week n.  The first day is always a Sunday unless
// n = 1, in which case the first day is January 1st.
export const getDateByWeekNumber = (weekNumber, year) => {
  if (weekNumber === 1) return new Date(year, 0, 1);
  // later this will vary based on locale
  const firstWeekDaysInPrevYear = new Date(year, 0, 1).getDay();
  return new Date(year, 0, (weekNumber - 1) * 7 - (firstWeekDaysInPrevYear - 1));
};

// when a date belongs to week 1 of the next year, the year needs to be adjusted upwards
export const yearAdjustment = date => {
  // there is an exception for 9999 since 10000 is out of bounds
  if (date.getFullYear() === 9999) return 0;
  const firstDateOfWeek = new Date(date.getFullYear(), date.getMonth(), date.getDate() - date.getDay());
  const lastDateOfWeek = new Date(firstDateOfWeek);
  lastDateOfWeek.setDate(lastDateOfWeek.getDate() + 6);
  return lastDateOfWeek.getFullYear() - date.getFullYear();
};

// Returns the number of weeks in the year which the given date belongs to
export const getNumberOfWeeks = date => {
  // to avoid assigning dates in 9999 into an out of bounds year we always give it 53 weeks
  if (date.getFullYear() === 9999) return 53;
  // adjustment is necessary because the display date can be different than the one shown on the UI
  // for example December 28th 2021 will be shown as "Week 1 2022"
  const year = date.getFullYear() + yearAdjustment(date);
  const lastDayOfWeek = Weekday.Saturday; // later this will vary depending on locale
  const secondLastDayOfWeek = Weekday.Friday;
  const firstDayOfYear = new Date(year, 0, 1).getDay();
  return firstDayOfYear === lastDayOfWeek ||
  // leap years have 52 full weeks and 2 extra days so there are two ways to get a 53rd week:
  // if the first week only has one day or only two days
  isLeapYear(year) && firstDayOfYear === secondLastDayOfWeek ? 53 : 52;
};
export const getWeekNumber = date => {
  // if the last day of the week belongs to a different year, this date is considered week 1 of next year
  if (yearAdjustment(date) > 0) return 1;
  const firstWeekDaysInPrevYear = new Date(date.getFullYear(), 0, 1).getDay();
  return Math.floor((getDayNumber(date) + firstWeekDaysInPrevYear - 1) / 7) + 1;
};

// A year will need to be adjusted if the locale is Thai or part of the week belongs to the next year
export const getYearOfWeek = (date, locale) => {
  const currentYear = date.getFullYear() + yearAdjustment(date);
  return isThaiLocale(locale) ? convertGregorianYearToThaiYear(currentYear) : currentYear;
};
export const stdTimezoneOffset = date => {
  // The actual date-time doesn't matter.
  if (!date) {
    date = new Date();
  } else {
    date = new Date(date.getTime());
  }
  // In javascript the minimum and maximum dates are:
  //   Min: Tue Apr 20 -271821 05:00:00 GMT-0400 (Eastern Daylight Time)
  //   Max: Fri Sep 12 275760 20:00:00 GMT-0400 (Eastern Daylight Time).
  // When the minimum date is specified, date.setMonth(0) makes it invalid date value
  // and this function returns NaN. The following if-condition block is a workaround.
  // The year of the minimum date value is -271821 where human being didn't exist so
  // that timezone offset is meaningless. In order to get a timezone offset, the
  // year -271820 can be used instead of -271821.
  if (date.getFullYear() === MINIMUM_YEAR) {
    date.setFullYear(MINIMUM_YEAR + 1);
  }
  // Set to January so that we get either Standard or Daylight time.
  date.setMonth(0);
  const offset1 = date.getTimezoneOffset();
  // Set to June so that we get the other of Standard or Daylight time.
  date.setMonth(5);
  const offset2 = date.getTimezoneOffset();
  // The offset for Daylight time will always be less that the offset for Standard time.
  // Return the Standard time offset (which is the 'raw offset').
  return offset1 > offset2 ? offset1 : offset2;
};
export const dstTimezoneOffset = date => {
  // The actual date-time doesn't matter.
  if (!date) {
    date = new Date();
  } else {
    date = new Date(date.getTime());
  }
  // In javascript the minimum and maximum dates are:
  //   Min: Tue Apr 20 -271821 05:00:00 GMT-0400 (Eastern Daylight Time)
  //   Max: Fri Sep 12 275760 20:00:00 GMT-0400 (Eastern Daylight Time).
  // When the minimum date is specified, date.setMonth(0) makes it invalid date value
  // and this function returns NaN. The following if-condition block is a workaround.
  // The year of the minimum date value is -271821 where human being didn't exist so
  // that timezone offset is meaningless. In order to get a timezone offset, the
  // year -271820 can be used instead of -271821.
  if (date.getFullYear() === MINIMUM_YEAR) {
    date.setFullYear(MINIMUM_YEAR + 1);
  }
  // Set to January so that we get either Standard or Daylight time.
  date.setMonth(0);
  const offset1 = date.getTimezoneOffset();
  // Set to June so that we get the other of Standard or Daylight time.
  date.setMonth(5);
  const offset2 = date.getTimezoneOffset();
  // The offset for Daylight time will always be less that the offset for Standard time.
  // Return the Standard time offset (which is the 'raw offset').
  return offset1 < offset2 ? offset1 : offset2;
};
export const inDaylightTime = date => {
  if (!date) {
    date = new Date();
  }
  return date.getTimezoneOffset() < stdTimezoneOffset(date);
};
//# sourceMappingURL=DateUtil.js.map